import type { DataProvider, Identifier, SortPayload } from "ra-core";

import type { Company, Contact, Deal, Sale, Tag } from "../types";
import type { ExportLookups } from "./types";

const PAGE_SIZE = 500;
const ID_CHUNK = 150;

/** Every record of the list (all pages), with its current filters and sort. */
export const fetchAllRecords = async <RecordType extends { id: Identifier }>(
  dataProvider: DataProvider,
  resource: string,
  filter: Record<string, unknown>,
  sort: SortPayload,
): Promise<RecordType[]> => {
  const records: RecordType[] = [];
  for (let page = 1; ; page++) {
    const { data, total } = await dataProvider.getList<RecordType>(resource, {
      filter,
      sort,
      pagination: { page, perPage: PAGE_SIZE },
    });
    records.push(...data);
    if (
      data.length < PAGE_SIZE ||
      (total !== undefined && records.length >= total)
    ) {
      return records;
    }
  }
};

/** Names of the users, companies, contacts and tags the exported records point to. */
export const fetchExportLookups = async (
  dataProvider: DataProvider,
  records: Partial<Company & Contact & Deal>[],
): Promise<ExportLookups> => {
  const ids = (pick: (record: Partial<Company & Contact & Deal>) => unknown) =>
    [...new Set(records.flatMap((record) => [pick(record)].flat()))].filter(
      (id): id is Identifier => id != null,
    );
  const [sales, companies, contacts, tags] = await Promise.all([
    getNames<Sale>(
      dataProvider,
      "sales",
      ids((record) => record.sales_id),
      (sale) => `${sale.first_name} ${sale.last_name}`.trim(),
    ),
    getNames<Company>(
      dataProvider,
      "companies",
      ids((record) => [record.company_id, ...(record.company_ids ?? [])]),
      (company) => company.name,
    ),
    getNames<Contact>(
      dataProvider,
      "contacts",
      ids((record) => record.contact_ids),
      (contact) =>
        `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim(),
    ),
    getNames<Tag>(
      dataProvider,
      "tags",
      ids((record) => record.tags),
      (tag) => tag.name,
    ),
  ]);
  return { sales, companies, contacts, tags };
};

const getNames = async <RecordType extends { id: Identifier }>(
  dataProvider: DataProvider,
  resource: string,
  ids: Identifier[],
  nameOf: (record: RecordType) => string,
) => {
  const names = new Map<Identifier, string>();
  for (let start = 0; start < ids.length; start += ID_CHUNK) {
    const { data } = await dataProvider.getMany<RecordType>(resource, {
      ids: ids.slice(start, start + ID_CHUNK),
    });
    data.forEach((record) => names.set(record.id, nameOf(record)));
  }
  return names;
};
